import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, MessageSquare, User, Check, Clock, Ban, AtSign, Trash2, Shield, Hammer } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { friendsApi } from '@/api/friends';
import { usersApi, adminApi } from '@/api/users';
import { messagesApi } from '@/api/messages';
import { channelsApi } from '@/api/channels';
import { useAuthStore } from '@/store/authStore';
import { useBlockedUsersStore } from '@/store/blockedUsersStore';
import { useChatStore } from '@/store/chatStore';
import { useChannelRole } from '@/hooks/useChannelRole';
import { useMobile } from '@/pages/chat/ChatPage';
import Avatar from '@/components/ui/Avatar';
import NickName from '@/components/ui/NickName';

interface Props {
  userId: string;
  username: string;
  displayName: string;
  position: { x: number; y: number };
  onClose: () => void;
  /** If opened from a message, enables "Удалить сообщение" for admins/mods. */
  messageId?: string;
  /** Enables channel-scoped actions (mention inject, promote to moderator). */
  channelId?: string;
  /** DM peer for mention inject scope. If neither channelId nor peerUserId set, mention inject is hidden. */
  peerUserId?: string;
  /** Embed (russnt iframe): прячем full-page навигацию (профиль /u/, DM /dm/) —
   *  у app-страниц frame-ancestors 'none', а внутри iframe это рендерит весь
   *  app в крошечном фрейме без возврата. Остаётся mention/блок/мини-профиль. */
  embedMode?: boolean;
}

export default function UserContextMenu({ userId, username, displayName, position, onClose, messageId, channelId, peerUserId, embedMode }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { setShowSidebar } = useMobile();
  const isBlocked = useBlockedUsersStore((s) => s.isBlocked(userId));
  const toggleBlock = useBlockedUsersStore((s) => s.toggle);
  const injectMention = useChatStore((s) => s.injectMention);
  const myChannelRole = useChannelRole(channelId ?? null);
  const ref = useRef<HTMLDivElement>(null);
  const [showProfile, setShowProfile] = useState(false);

  const isGlobalAdmin = user?.globalRole === 'SUPER_ADMIN' || user?.globalRole === 'ADMIN';
  const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';
  const isChannelAdminOrMod = myChannelRole === 'OWNER' || myChannelRole === 'ADMIN' || myChannelRole === 'MODERATOR';
  const canModerate = isGlobalAdmin || isChannelAdminOrMod;
  const canAssignMod = isSuperAdmin || myChannelRole === 'OWNER';
  const scopeKey = channelId ?? peerUserId ?? '';

  const deleteMessageMut = useMutation({
    mutationFn: () => messagesApi.deleteMessage(messageId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      onClose();
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Не удалось удалить'),
  });

  const globalBanMut = useMutation({
    mutationFn: () => adminApi.banUser(userId),
    onSuccess: () => { alert(`${username} заблокирован глобально`); onClose(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Не удалось'),
  });

  const promoteModMut = useMutation({
    mutationFn: () => channelsApi.setMemberRole(channelId!, userId, 'MODERATOR'),
    onSuccess: () => { alert(`${username} теперь модератор`); onClose(); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Не удалось'),
  });

  const clearChatMut = useMutation({
    mutationFn: () => channelsApi.clearMessages(channelId!),
    onSuccess: (r: any) => {
      alert(`Очищено ${r?.count ?? '?'} сообщений`);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      onClose();
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Не удалось'),
  });

  const { data: friendStatus } = useQuery({
    queryKey: ['friend-status', userId],
    queryFn: () => friendsApi.getStatus(userId),
    enabled: userId !== user?.id,
  });

  const { data: profile } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersApi.getProfile(userId),
    enabled: showProfile,
  });

  const addFriendMutation = useMutation({
    mutationFn: () => friendsApi.sendRequest(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friend-status', userId] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
    },
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (userId === user?.id) return null;

  const isFriend = friendStatus?.status === 'accepted';
  const isPending = friendStatus?.status === 'pending';

  // Adjust position to stay on screen
  const menuWidth = showProfile ? 280 : 220;
  const menuHeight = showProfile ? 350 : 200;
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - menuWidth),
    top: Math.min(position.y, window.innerHeight - menuHeight),
    zIndex: 100,
  };

  // Profile view
  if (showProfile) {
    return (
      <div ref={ref} style={style} className="w-72 bg-surface-900 border border-surface-700 rounded-xl shadow-2xl animate-fade-in overflow-hidden">
        {/* Profile header */}
        <div className="bg-gradient-to-b from-brand-900/50 to-surface-900 p-4 text-center">
          <Avatar src={profile?.avatarUrl} username={username} size="lg" className="mx-auto mb-2" />
          <p className="text-surface-100 font-semibold"><NickName user={profile ?? { displayName }} /></p>
          <p className="text-surface-500 text-xs font-mono">@{username}</p>
          {profile?.status === 'ONLINE' && (
            <span className="inline-flex items-center gap-1 mt-1 text-xs text-green-400">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              В сети
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="p-2 border-t border-surface-800 space-y-0.5">
          {/* DM remains full-page navigation, so it is unavailable in iframe embeds. */}
          {!embedMode && (
            <button
              onClick={() => {
                setShowSidebar(false);
                navigate(`/dm/${userId}`);
                onClose();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 rounded-lg transition-colors"
            >
              <MessageSquare size={14} />
              Личное сообщение
            </button>
          )}

          {/* Add friend */}
          {!isFriend && !isPending && (
            <button
              onClick={() => { addFriendMutation.mutate(); }}
              disabled={addFriendMutation.isPending}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 rounded-lg transition-colors"
            >
              <UserPlus size={14} />
              {addFriendMutation.isPending ? 'Отправка...' : addFriendMutation.isSuccess ? 'Отправлено!' : 'Добавить в друзья'}
            </button>
          )}

          {isPending && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-yellow-400">
              <Clock size={14} />
              Заявка отправлена
            </div>
          )}

          {isFriend && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-green-400">
              <Check size={14} />
              В друзьях
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default context menu
  return (
    <div ref={ref} style={style} className="w-52 bg-surface-900 border border-surface-700 rounded-xl shadow-2xl py-1.5 animate-fade-in">
      {/* Header */}
      <div className="px-3 py-2 border-b border-surface-800">
        <p className="text-surface-100 text-sm font-semibold truncate">{displayName}</p>
        <p className="text-surface-500 text-xs font-mono">@{username}</p>
      </div>

      {/* Actions */}
      <div className="py-1">
        {/* Mention inject — "Написать" (copies @nick into input) */}
        {scopeKey && (
          <button
            onClick={() => { injectMention(scopeKey, username); onClose(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
          >
            <AtSign size={14} />
            Написать @{username}
          </button>
        )}

        {/* DM (скрыт в эмбеде — увёл бы iframe на full-page DM) */}
        {!embedMode && (
        <button
          onClick={() => {
            setShowSidebar(false);
            navigate(`/dm/${userId}`);
            onClose();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
        >
          <MessageSquare size={14} />
          Личное сообщение
        </button>
        )}

        {/* Add friend */}
        {!isFriend && !isPending && (
          <button
            onClick={() => { addFriendMutation.mutate(); }}
            disabled={addFriendMutation.isPending}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
          >
            <UserPlus size={14} />
            {addFriendMutation.isPending ? 'Отправка...' : addFriendMutation.isSuccess ? 'Отправлено!' : 'Добавить в друзья'}
          </button>
        )}

        {isPending && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-yellow-400">
            <Clock size={14} />
            Заявка отправлена
          </div>
        )}

        {isFriend && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-green-400">
            <Check size={14} />
            В друзьях
          </div>
        )}

        {embedMode && (
          <button
            onClick={() => setShowProfile(true)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
          >
            <User size={14} />
            Мини-профиль @{username}
          </button>
        )}

        {/* View profile (скрыт в эмбеде — /u/* имеет frame-ancestors 'none') */}
        {!embedMode && (
        <button
          onClick={() => { navigate(`/u/${username}`); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
        >
          <User size={14} />
          Профиль @{username}
        </button>
        )}

        {/* Block / unblock (client-side only) */}
        <button
          onClick={() => { toggleBlock(userId); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 hover:text-red-300 transition-colors border-t border-surface-800 mt-1"
        >
          <Ban size={14} />
          {isBlocked ? 'Разблокировать' : 'Заблокировать'}
        </button>

        {/* ── Admin actions ─────────────────────────────────────────── */}
        {canModerate && (
          <>
            <div className="border-t border-surface-800 mt-1 px-3 py-1 text-[10px] uppercase text-surface-500 tracking-wider">
              Модерация
            </div>
            {messageId && (
              <button
                onClick={() => {
                  if (confirm('Удалить это сообщение?')) deleteMessageMut.mutate();
                }}
                disabled={deleteMessageMut.isPending}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-300 hover:bg-red-900/40 transition-colors"
              >
                <Trash2 size={14} />
                Удалить сообщение
              </button>
            )}
            {channelId && (
              <button
                onClick={() => {
                  if (confirm('Очистить весь чат канала? Все сообщения будут помечены удалёнными.')) clearChatMut.mutate();
                }}
                disabled={clearChatMut.isPending}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-300 hover:bg-red-900/40 transition-colors"
              >
                <Trash2 size={14} />
                Очистить чат
              </button>
            )}
            {canAssignMod && channelId && (
              <button
                onClick={() => {
                  if (confirm(`Назначить ${username} модератором канала?`)) promoteModMut.mutate();
                }}
                disabled={promoteModMut.isPending}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-300 hover:bg-amber-900/30 transition-colors"
              >
                <Shield size={14} />
                Сделать модератором
              </button>
            )}
            {isGlobalAdmin && (
              <button
                onClick={() => {
                  if (confirm(`ЗАБЛОКИРОВАТЬ ${username} НА ВСЕЙ ПЛАТФОРМЕ?`)) {
                    globalBanMut.mutate();
                  }
                }}
                disabled={globalBanMut.isPending}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-900/50 transition-colors"
              >
                <Hammer size={14} />
                Заблокировать для всех
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
